import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import RequireStore from "@/components/RequireStore";
import { formatCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, FileDown } from "lucide-react";

const now = new Date();

export default function Report() {
  const { storeId, currentStore } = useStore();
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);

  useEffect(() => {
    if (storeId === "all") { setData(null); return; }
    api.get(`/report/${year}`, { params: { store_id: storeId } }).then(({ data }) => setData(data));
  }, [year, storeId]);

  const exportCsv = () => {
    if (!data) return;
    const header = ["Mese", "Incassi", "Versamenti", "Ricariche", "Bar", "Shop", "Riconciliazioni", "Saldo finale"];
    const rows = data.months.map((m) => [
      m.month_name, m.totals.incasso, m.totals.versato, m.totals.ricariche, m.totals.bar, m.totals.shop, m.totals.riconciliazioni, m.saldo_finale,
    ]);
    rows.push(["TOTALE ANNO", data.annual.incasso, data.annual.versato, data.annual.ricariche, data.annual.bar, data.annual.shop, data.annual.riconciliazioni, ""]);
    const csv = [header, ...rows].map((r) => r.join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `report_${year}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <RequireStore>
    <div className="space-y-5" data-testid="report-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">Report Annuale</h1>
          <p className="text-sm text-muted-foreground">{currentStore?.name} · totali mese per mese e riepilogo dell'anno.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="icon" onClick={() => setYear(year - 1)} data-testid="report-year-prev"><ChevronLeft className="h-5 w-5" /></Button>
          <div className="min-w-[100px] rounded-lg border border-border bg-card px-4 py-2 text-center font-display text-lg font-bold" data-testid="report-year-label">{year}</div>
          <Button variant="secondary" size="icon" onClick={() => setYear(year + 1)} data-testid="report-year-next"><ChevronRight className="h-5 w-5" /></Button>
          <Button variant="secondary" className="gap-2" onClick={exportCsv} data-testid="report-export-btn"><FileDown className="h-4 w-4" /> CSV</Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full text-sm" data-testid="report-table">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-3">Mese</th>
              <th className="px-3 py-3 text-right">Incassi</th>
              <th className="px-3 py-3 text-right">Versamenti</th>
              <th className="hidden px-3 py-3 text-right sm:table-cell">Ricariche</th>
              <th className="hidden px-3 py-3 text-right sm:table-cell">Bar</th>
              <th className="hidden px-3 py-3 text-right sm:table-cell">Shop</th>
              <th className="hidden px-3 py-3 text-right md:table-cell">Riconc.</th>
              <th className="px-3 py-3 text-right">Saldo finale</th>
            </tr>
          </thead>
          <tbody>
            {data?.months.map((m) => (
              <tr key={m.month} className={`border-b border-border/50 ${m.has_data ? "" : "opacity-40"}`}>
                <td className="px-3 py-3 font-medium">{m.month_name}</td>
                <td className="px-3 py-3 text-right tabular-nums">{formatCurrency(m.totals.incasso)}</td>
                <td className="px-3 py-3 text-right tabular-nums">{formatCurrency(m.totals.versato)}</td>
                <td className="hidden px-3 py-3 text-right tabular-nums sm:table-cell">{formatCurrency(m.totals.ricariche)}</td>
                <td className="hidden px-3 py-3 text-right tabular-nums sm:table-cell">{formatCurrency(m.totals.bar)}</td>
                <td className="hidden px-3 py-3 text-right tabular-nums sm:table-cell">{formatCurrency(m.totals.shop)}</td>
                <td className="hidden px-3 py-3 text-right tabular-nums md:table-cell">{formatCurrency(m.totals.riconciliazioni)}</td>
                <td className="px-3 py-3 text-right font-semibold tabular-nums text-primary">{m.has_data ? formatCurrency(m.saldo_finale) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="report-annual-totals">
          <Tot label="Incassi anno" v={data.annual.incasso} />
          <Tot label="Versamenti anno" v={data.annual.versato} />
          <Tot label="Bar anno" v={data.annual.bar} />
          <Tot label="Shop anno" v={data.annual.shop} />
          <Tot label="Ricariche anno" v={data.annual.ricariche} />
          <Tot label="Riconciliazioni anno" v={data.annual.riconciliazioni} />
        </div>
      )}
    </div>
    </RequireStore>
  );
}

function Tot({ label, v }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-lg font-extrabold tabular-nums">{formatCurrency(v)}</div>
    </div>
  );
}
